import React, { useEffect, useState } from "react";
import {
	Tabs,
	Tab,
	Col,
	ListGroup,
	Row,
	Card,
	Placeholder,
} from "react-bootstrap";
import Modal from "../../common/components/ThemedModal";
import { getSnapshotsByTeam } from "../../../services/snapshotService";
import { getGameModeAssignment } from "../../../services/adminService";
import { getTeam } from "../../../services/teamService";
import ReactCountryFlag from "react-country-flag";
import getCountryISO2 from "country-iso-3-to-2";
import { getResultsBySportAndSeries } from "../../../services/resultService";
import { MdLeaderboard } from "react-icons/md";
import { getTeamResults } from "../../../services/teamResultService";
import {
	initialTeamWithRider,
	LeaderboardTeam,
	ResultWithRider,
	SnapshotWithEventandRider,
	TeamResultWithRider,
	TeamWithRider,
} from "../../../types/model";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import {
	fetchBrandTeamsThunk,
	fetchSeasonsThunk,
} from "../../../store/thunks/fantasyThunk";
import PointsBreakdownMultiplierMode from "./PointsBreakdownComponents/PointsBreakdownMultiplierMode";
import PointsBreakdownBudgetMode from "./PointsBreakdownComponents/PointsBreakdownBudgetMode";
import { GameModeAssignemts } from "../../../constants/constants";
import PointsBreakdownPickandRank from "./PointsBreakdownComponents/PointsBreakdownPickandRank";
import { getTotalPointsForTeam } from "../../../utils/helper";

interface PointsBreakdownProps {
	leaderboardTeam: LeaderboardTeam;
	showModal: boolean;
	handleClose: () => void;
}

const PointsBreakdown = ({
	leaderboardTeam,
	showModal,
	handleClose,
}: PointsBreakdownProps) => {
	const { brandTeams, seasons } = useAppSelector(
		(state: RootState) => state.fantasy,
	);
	const dispatch = useAppDispatch();

	const [snapshots, setSnapshots] = useState<SnapshotWithEventandRider[]>([]);
	const [gameModeAssignment, setGameModeAssignment] = useState(null);
	const [numberMen, setNumberMen] = useState(0);
	const [numberWomen, setNumberWomen] = useState(0);
	const [team, setTeam] = useState<TeamWithRider>(initialTeamWithRider);
	const [teamResults, setTeamResults] = useState<TeamResultWithRider[]>([]);
	const [selectedEvent, setSelectedEvent] = useState<string>("");
	const [results, setResults] = useState<ResultWithRider[]>([]);
	const [allResults, setAllResults] = useState<ResultWithRider[]>([]);

	const season = seasons.find((season) => season._id === team.season);
	const is2024 = season?.name === "2024 Season";

	useEffect(() => {
		if (leaderboardTeam?.teamId) {
			fetchTeam();
			if (brandTeams.length === 0) {
				fetchBrandTeams();
			}

			if (seasons.length === 0) {
				fetchSeasons();
			}
		}
	}, [leaderboardTeam]);

	useEffect(() => {
		if (snapshots.length > 0) {
			fetchTeamResult(team);
			if (selectedEvent === "") {
				setSelectedEvent(snapshots[0].event._id ?? "");
			}
		}
	}, [snapshots]);

	const fetchSnapshots = async (_team: TeamWithRider) => {
		try {
			const res = await getSnapshotsByTeam(_team._id);
			setSnapshots(res.data);
		} catch (error) {
			console.error("Error fetching snapshots:", error);
		}
	};

	const fetchTeamResult = async (_team: TeamWithRider) => {
		try {
			const res = await getTeamResults(_team._id);
			setTeamResults(res.data);
		} catch (error) {
			console.error("Error fetching team result:", error);
		}
	};

	const fetchGameModeAssignment = async (_team: TeamWithRider) => {
		try {
			const res = await getGameModeAssignment({
				sport: _team.sport,
				series: _team.series,
			});
			setGameModeAssignment(res.data[0].gameMode.name);
			setNumberMen(res.data[0].gameMode.numberOfMen);
			setNumberWomen(res.data[0].gameMode.numberOfWomen);
		} catch (error) {
			console.error("Error fetching game mode assignment:", error);
		}
	};

	const fetchBrandTeams = async () => {
		try {
			await dispatch(fetchBrandTeamsThunk());
		} catch (error) {
			console.error("Error fetching brand teams:", error);
		}
	};

	const fetchSeasons = async () => {
		try {
			await dispatch(fetchSeasonsThunk());
		} catch (error) {
			console.error("Error fetching seasons:", error);
		}
	};

	const fetchTeam = async () => {
		try {
			const res = await getTeam(leaderboardTeam.teamId);
			if (res.data) {
				setTeam(res.data);
				fetchSnapshots(res.data);
				fetchGameModeAssignment(res.data);
				fetchResults(res.data.sport, res.data.series, res.data.season);
			}
		} catch (error) {
			console.error("Error fetching team:", error);
		}
	};

	useEffect(() => {
		if (selectedEvent) {
			setCombinedResults(allResults);
		}
	}, [selectedEvent, allResults]);

	const fetchResults = async (
		sport: string,
		series: string,
		season: string,
	) => {
		try {
			const res = await getResultsBySportAndSeries(sport, series, season);
			setAllResults(res);
			setCombinedResults(res);
		} catch (error) {
			console.error("Error fetching results:", error);
		}
	};

	const setCombinedResults = (resultArray: ResultWithRider[]) => {
		const combinedResults: ResultWithRider[] = [];

		//Filter results by the selected event
		const filteredResults = resultArray.filter(
			(result: any) => result.event === selectedEvent,
		);
		filteredResults.forEach((result: ResultWithRider) => {
			const existingResult = combinedResults.find(
				(item) => item.rider._id === result.rider._id,
			);
			if (existingResult) {
				existingResult.total_points =
					(existingResult.total_points ?? 0) + (result.total_points ?? 0);
				existingResult.rider = result.rider;
				existingResult.rider.status = result.rider.status;
				existingResult.rider.country = result.rider.country;
				existingResult.rider = result.rider;
				if (existingResult.qualifying_data) {
					existingResult.qualifying_data.points =
						(existingResult.qualifying_data.points ?? 0) +
						(result.qualifying_data?.points ?? 0);
				}
				existingResult.finals_data = existingResult.finals_data ?? {};
				existingResult.finals_data.points =
					(existingResult.finals_data.points ?? 0) +
					(result.finals_data?.points ?? 0);
				//Want to add in the rank
				existingResult.finals_data.rank =
					(existingResult.finals_data.rank ?? 0) +
					(result.finals_data?.rank ?? 0);
				existingResult.bestTrickRiderMen =
					existingResult.bestTrickRiderMen || result.bestTrickRiderMen;
				existingResult.bestTrickRiderWomen =
					existingResult.bestTrickRiderWomen || result.bestTrickRiderWomen;
				existingResult.PeoplesChoiceRiderMen =
					existingResult.PeoplesChoiceRiderMen || result.PeoplesChoiceRiderMen;
				existingResult.PeoplesChoiceRiderWomen =
					existingResult.PeoplesChoiceRiderWomen ||
					result.PeoplesChoiceRiderWomen;
			} else {
				combinedResults.push(result);
			}
		});
		setResults(combinedResults);
	};

	return (
		<Modal show={showModal} onHide={handleClose} size="lg">
			<Modal.Header closeButton className="border-0">
				<Modal.Title className="fw-bold">
					{" "}
					<MdLeaderboard className="mb-1 me-1" />
					POINTS BREAKDOWN
				</Modal.Title>
			</Modal.Header>
			<Modal.Body >
				{snapshots.length == 0 && (
					<Placeholder as="div" animation="glow" className="">
						<Placeholder
							xs={11}
							className="rounded-3"
							bg="light"
							style={{ height: "20rem", width: "10rem" }}
						/>
					</Placeholder>
				)}
				<Tabs
					defaultActiveKey={0}
					id="snapshot-tabs"
					variant="pills"
					onSelect={(key) => {
						const index = Number(key);
						const snapshot = snapshots[index];
						if (snapshot) {
							setSelectedEvent(snapshot.event._id ?? "");
						}
					}}
				>
					{/* Show User Team */}
					{snapshots.length > 0 ? (
						snapshots.map((snapshot, index) => (
							<Tab eventKey={index} title={snapshot.event.location} key={index}>
								<Card className="my-3 shadow bg-secondary text-light border-info">
									<Card.Header className="bg-secodary text-light">
										<div className="d-flex flex-row justify-content-between fs-3 fw-bold">
											<div className="">{snapshot.event.location}</div>
											{gameModeAssignment === GameModeAssignemts.Budget && (
												<div className="">
													total:{" "}
													{getTotalPointsForTeam(
														snapshot,
														teamResults,
														results,
													)}{" "}
													points
												</div>
											)}
										</div>
									</Card.Header>
									<Card.Body>
										{gameModeAssignment == "Mutliplier Mode" && (
											<ListGroup>
												{snapshot.riders.map((rider, riderIndex) => (
													<ListGroup.Item
														key={rider.rider._id}
														style={{ fontSize: "0.8rem" }}
													>
														<Row>
															<Col
																md={6}
																className="text-sm-start my-0 my-sm-0"
															>
																{getCountryISO2(
																	rider?.rider?.country ?? ""
																		? rider?.rider?.country
																				?.toUpperCase()
																				.trim() ?? ""
																		: "",
																) ? (
																	<ReactCountryFlag
																		countryCode={getCountryISO2(
																			rider?.rider?.country
																				? rider.rider.country
																						.toUpperCase()
																						.trim()
																				: "",
																		)}
																		svg
																		style={{ width: "3.5rem" }}
																	/>
																) : (
																	rider.rider.country
																)}
																<b>{rider.rider.last_name}</b>{" "}
																{rider.rider.first_name}
															</Col>
															<Col
																md={6}
																className="text-md-end text-center my-2 my-sm-0"
															>
																{rider.rider.status && rider.rider.status}
															</Col>
														</Row>
													</ListGroup.Item>
												))}
											</ListGroup>
										)}
										{gameModeAssignment === GameModeAssignemts.Budget && (
											<div className="">
												<ListGroup >
													{snapshot.riders.map((rider, riderIndex) => (
														<ListGroup.Item
															className="bg-dark text-light border-info"
															key={rider.rider._id}
															style={{ fontSize: "0.8rem" }}
														>
															<Row>
																<Col
																	md={6}
																	className="text-sm-start my-0 my-sm-0"
																>
																	{getCountryISO2(
																		(rider?.rider?.country ?? "")
																			.toUpperCase()
																			.trim(),
																	) ? (
																		<ReactCountryFlag
																			countryCode={getCountryISO2(
																				(rider?.rider?.country ?? "")
																					.toUpperCase()
																					.trim(),
																			)}
																			svg
																			style={{ width: "3.5rem" }}
																		/>
																	) : (
																		rider.rider.country
																	)}
																	<b>{rider.rider.last_name}</b>{" "}
																	{rider.rider.first_name}
																</Col>
																<Col
																	md={6}
																	className="text-md-end text-center my-2 my-sm-0"
																>
																	{rider.rider.status &&
																		rider.rider.status.toLocaleUpperCase()}
																</Col>
															</Row>
														</ListGroup.Item>
													))}
												</ListGroup>
												{snapshot.selectedTeam && (
													<Card
														className="my-3 shadow bg-dark text-light border-info"
														style={{ fontSize: "0.9rem" }}
													>
														<Card.Header className="text-light py-1 border-info">
															Selected Team
														</Card.Header>
														<Card.Body className="text-center fw-bold py-2">
															{is2024
																? snapshot.selectedTeam
																: brandTeams
																		.find(
																			(brandTeam) =>
																				brandTeam._id === snapshot.selectedTeam,
																		)
																		?.name.toLocaleUpperCase()}
														</Card.Body>
													</Card>
												)}
											</div>
										)}
										{(gameModeAssignment === "Pick and Rank" ||
											gameModeAssignment === "Pick and Rank Hardline") && (
											<>
												<ListGroup>
													{/* Display Men Riders */}
													{snapshot.riders
														.filter((rider) => rider.rider?.gender === "m")
														.map((rider, riderIndex) => (
															<ListGroup.Item
																key={rider.rider._id}
																style={{ fontSize: "0.8rem" }}
															>
																<Row>
																	<Col
																		md={6}
																		className="text-sm-start my-0 my-sm-0"
																	>
																		{/* Show chosen rider rank */}
																		{rider.rank}.{" "}
																		{getCountryISO2(
																			(rider?.rider?.country ?? "")
																				.toUpperCase()
																				.trim(),
																		) ? (
																			<ReactCountryFlag
																				countryCode={getCountryISO2(
																					(rider?.rider?.country ?? "")
																						.toUpperCase()
																						.trim(),
																				)}
																				svg
																				style={{ width: "3.5rem" }}
																			/>
																		) : (
																			rider.rider?.country
																		)}
																		<b>{rider.rider?.last_name}</b>{" "}
																		{rider.rider?.first_name}
																		<span
																			className=" ms-1"
																			style={{ fontSize: "0.6rem" }}
																		>
																			({rider.rider?.gender})
																		</span>
																	</Col>
																	<Col
																		md={6}
																		className="text-md-end text-center my-2 my-sm-0"
																	>
																		{rider.rider.status && rider.rider.status}
																	</Col>
																</Row>
															</ListGroup.Item>
														))}
												</ListGroup>
												<ListGroup className="mt-3">
													{/* Display Women Riders */}
													{snapshot.riders
														.filter((rider) => rider.rider?.gender === "f")
														.map((rider, riderIndex) => (
															<ListGroup.Item
																key={rider.rider._id}
																style={{ fontSize: "0.8rem" }}
															>
																<Row>
																	<Col
																		md={6}
																		className="text-sm-start my-0 my-sm-0"
																	>
																		{/* Show chosen rider rank */}
																		{rider.rank}.{" "}
																		{getCountryISO2(
																			(rider?.rider?.country ?? "")
																				.toUpperCase()
																				.trim(),
																		) ? (
																			<ReactCountryFlag
																				countryCode={getCountryISO2(
																					(rider?.rider?.country ?? "")
																						.toUpperCase()
																						.trim(),
																				)}
																				svg
																				style={{ width: "3.5rem" }}
																			/>
																		) : (
																			rider.rider.country
																		)}
																		<b>{rider.rider.last_name}</b>{" "}
																		{rider.rider.first_name}
																		<span
																			className=" ms-1"
																			style={{ fontSize: "0.6rem" }}
																		>
																			({rider.rider.gender})
																		</span>
																	</Col>
																	<Col
																		md={6}
																		className="text-md-end text-center my-2 my-sm-0"
																	>
																		{rider.rider.status && rider.rider.status}
																	</Col>
																</Row>
															</ListGroup.Item>
														))}
												</ListGroup>
												{gameModeAssignment === "Pick and Rank Hardline" && (
													<Card
														className="my-3 shadow bg-dark text-light border-info"
														style={{ fontSize: "0.9rem" }}
													>
														<Card.Header className="text-light py-1 border-info">
															Bonus Riders
														</Card.Header>
														<Card.Body className="text-center fw-bold py-2">
															{/* Display bonus riders here */}
															<Row className="justify-content-center">
																<Col xs={12} md={6} className="mb-2">
																	{snapshot.PeoplesChoiceRiderMen && (
																		<Card className="bg-info text-dark mb-2 shadow-sm">
																			<Card.Body className="py-2 px-3">
																				<div className="fw-bold">
																					People's Choice{" "}
																					<span className="badge bg-primary ms-2">
																						Men
																					</span>
																				</div>
																				<div>
																					{
																						snapshot.PeoplesChoiceRiderMen
																							.first_name
																					}{" "}
																					<b>
																						{
																							snapshot.PeoplesChoiceRiderMen
																								.last_name
																						}
																					</b>
																				</div>
																			</Card.Body>
																		</Card>
																	)}
																	{snapshot.bestTrickRiderMen && (
																		<Card className="bg-info text-dark mb-2 shadow-sm">
																			<Card.Body className="py-2 px-3">
																				<div className="fw-bold">
																					Best Trick{" "}
																					<span className="badge bg-primary ms-2">
																						Men
																					</span>
																				</div>
																				<div>
																					{
																						snapshot.bestTrickRiderMen
																							.first_name
																					}{" "}
																					<b>
																						{
																							snapshot.bestTrickRiderMen
																								.last_name
																						}
																					</b>
																				</div>
																			</Card.Body>
																		</Card>
																	)}
																</Col>
																<Col xs={12} md={6} className="mb-2">
																	{snapshot.PeoplesChoiceRiderWomen && (
																		<Card className="bg-info text-dark mb-2 shadow-sm">
																			<Card.Body className="py-2 px-3">
																				<div className="fw-bold">
																					People's Choice{" "}
																					<span className="badge bg-dark ms-2">
																						Women
																					</span>
																				</div>
																				<div>
																					{
																						snapshot.PeoplesChoiceRiderWomen
																							.first_name
																					}{" "}
																					<b>
																						{
																							snapshot.PeoplesChoiceRiderWomen
																								.last_name
																						}
																					</b>
																				</div>
																			</Card.Body>
																		</Card>
																	)}
																	{snapshot.bestTrickRiderWomen && (
																		<Card className="bg-info text-dark mb-2 shadow-sm">
																			<Card.Body className="py-2 px-3">
																				<div className="fw-bold">
																					Best Trick{" "}
																					<span className="badge bg-dark ms-2">
																						Women
																					</span>
																				</div>
																				<div className="fw-normal">
																					{
																						snapshot.bestTrickRiderWomen
																							.first_name
																					}{" "}
																					<b>
																						{
																							snapshot.bestTrickRiderWomen
																								.last_name
																						}
																					</b>
																				</div>
																			</Card.Body>
																		</Card>
																	)}
																</Col>
															</Row>
														</Card.Body>
													</Card>
												)}
											</>
										)}
									</Card.Body>
								</Card>

								{/* Show the results for each rider */}
								<Card
									className="my-3 shadow bg-secondary text-light border-info"
									style={{ overflow: "auto" }}
								>
									<Card.Header className="border-info fs-4 fw-bold">
										RESULTS
									</Card.Header>
									<Card.Body>
										{gameModeAssignment === GameModeAssignemts.Multiplier && (
											<PointsBreakdownMultiplierMode
												results={results}
												snapshot={snapshot}
												team={team}
											/>
										)}
										{gameModeAssignment === GameModeAssignemts.Budget && (
											<PointsBreakdownBudgetMode
												results={results}
												snapshot={snapshot}
												teamResults={teamResults}
												showSemisPoints={is2024}
											/>
										)}
										{(gameModeAssignment === GameModeAssignemts.PickAndRank ||
											gameModeAssignment ===
												GameModeAssignemts.PickAndRankHardline) && (
											<PointsBreakdownPickandRank
												results={results}
												snapshot={snapshot}
												numberMen={numberMen}
												numberWomen={numberWomen}
											/>
										)}
										{gameModeAssignment ===
											GameModeAssignemts.PickAndRankHardline && (
											//actually build it out here.
											<Card className="mt-4">
												<Card.Header className="bg-secondary text-light border-info">
													Bonus Points
												</Card.Header>
												<Card.Body>
													{results
														.filter(
															(result) =>
																result.bestTrickRiderMen === true ||
																result.bestTrickRiderWomen === true ||
																result.PeoplesChoiceRiderMen === true ||
																result.PeoplesChoiceRiderWomen === true,
														)
														.map((result, index) => (
															<div key={index} className="mb-2">
																{snapshot.bestTrickRiderMen?._id ===
																	result.rider._id &&
																	result.bestTrickRiderMen &&
																	snapshot.event._id === result.event && (
																		<>
																			<strong>
																				{result.rider.first_name}{" "}
																				{result.rider.last_name}:
																			</strong>{" "}
																			<span className="badge bg-info mx-2">
																				Best Trick Men
																			</span>
																			100 points
																		</>
																	)}
																{snapshot.bestTrickRiderWomen?._id ===
																	result.rider._id &&
																	result.bestTrickRiderWomen &&
																	snapshot.event._id === result.event && (
																		<>
																			<strong>
																				{result.rider.first_name}{" "}
																				{result.rider.last_name}:
																			</strong>{" "}
																			<span className="badge bg-info mx-2">
																				Best Trick Women
																			</span>
																			100 points
																		</>
																	)}
																{snapshot.PeoplesChoiceRiderMen?._id ===
																	result.rider._id &&
																	result.PeoplesChoiceRiderMen &&
																	snapshot.event._id === result.event && (
																		<>
																			<strong>
																				{result.rider.first_name}{" "}
																				{result.rider.last_name}:
																			</strong>{" "}
																			<span className="badge bg-info mx-2">
																				People's Choice Men
																			</span>
																			100 points
																		</>
																	)}
																{snapshot.PeoplesChoiceRiderWomen?._id ===
																	result.rider._id &&
																	result.PeoplesChoiceRiderWomen &&
																	snapshot.event._id === result.event && (
																		<>
																			<strong>
																				{result.rider.first_name}{" "}
																				{result.rider.last_name}:
																			</strong>{" "}
																			<span className="badge bg-info mx-2">
																				People's Choice Women
																			</span>
																			100 points
																		</>
																	)}
															</div>
														))}
													{/* Bonus points details go here */}
												</Card.Body>
											</Card>
										)}
									</Card.Body>
								</Card>
							</Tab>
						))
					) : (
						<Placeholder as="div" animation="glow" className="">
							<Placeholder
								xs={11}
								className="rounded-3"
								bg="light"
								style={{ height: "20rem", width: "10rem" }}
							/>
						</Placeholder>
					)}
				</Tabs>
			</Modal.Body>
		</Modal>
	);
};

export default PointsBreakdown;
